import React, { useId, useRef, useState } from 'react';
import Button from '@components/atoms/button/button';
import clsx from 'clsx';
import { Swiper, SwiperProps } from 'swiper/react';
import { FreeMode, Mousewheel, A11y, Navigation, Keyboard } from 'swiper';
import 'swiper/css';
import useWindowSize from '@hooks/use-window-size';
import { ProductCardProps } from '@components/organisms/catalog/components/product-card/product-card-props';
import { useTranslation } from 'next-i18next';
import { useSwiperAccessibility } from '@hooks/swiper/use-swiper-accessibility';
import { SkipLink, SkipTarget } from '@components/atoms/skip-link/skip-link';
import { SLIDER_BREAKPOINTS, SLIDER_CONFIG } from './product-slider-config';
import styles from './product-slider.module.scss';

export interface ProductSliderProps<T> {
  products: Array<T>;
  swiperProps?: Partial<SwiperProps>;
  productCardProps?: Partial<ProductCardProps> & { pageType: ProductCardProps['pageType'] };
  onVisibilityChange?: ({ isVisible, index }: { isVisible: boolean; index: number }) => void;
  showAuthGuard?: boolean;
  wrapperClassName?: string;
  parentBlockId?: string;
  parentBlockTitle?: string;
  children: React.ReactNode;
}

const ProductSlider = <T,>({
  products,
  swiperProps,
  wrapperClassName,
  children,
  parentBlockTitle,
}: ProductSliderProps<T>) => {
  const { t: tA11y } = useTranslation('a11y');
  const swiperRef = useRef(null);
  const rawId = useId();
  const safeId = rawId.replace(/[^a-zA-Z0-9_-]/g, '-'); // sanitize for CSS
  const prevBtnClass = `custom-prev-btn-${safeId}`;
  const nextBtnClass = `custom-next-btn-${safeId}`;
  const [slideProgress, setSlideProgress] = useState(0);
  const { windowSize, isWiderThanMd } = useWindowSize();
  const windowWidth = windowSize.width;

  const normalizedParentBlockTitle = parentBlockTitle ? parentBlockTitle.toLowerCase().replace(/\s+/g, '-') : 'unknown';
  const hidePrevButton = slideProgress <= 0;
  const hideNextButton =
    slideProgress >= 1 ||
    (windowWidth >= SLIDER_BREAKPOINTS.tablet && products.length <= 3) ||
    (windowWidth >= SLIDER_BREAKPOINTS.desktop && products.length <= 4) ||
    (windowWidth >= SLIDER_BREAKPOINTS.largeDesktop && products.length <= 5);

  const config = SLIDER_CONFIG;
  // Show skip link only when: at the start + more content exists + desktop
  const shouldShowSkipLink = isWiderThanMd && hidePrevButton && !hideNextButton;
  const skipTargetId = `skip-slider-${safeId}`;

  // Calculate visible slides based on breakpoint
  const getVisibleSlides = () => {
    if (windowWidth >= SLIDER_BREAKPOINTS.largeDesktop) {
      return config.largeDesktopSlidesPerView;
    }
    if (windowWidth >= SLIDER_BREAKPOINTS.desktop) {
      return config.desktopSlidesPerView;
    }
    if (windowWidth >= SLIDER_BREAKPOINTS.tablet) {
      return config.tabletSlidesPerView;
    }
    return config.mobileSlidesPerView;
  };

  const visibleSlides = Math.floor(getVisibleSlides()); // Round down for aria-label

  const swiperLiveRef = useRef(null);
  const slidesContainerRef = useRef<HTMLDivElement>(null);
  const prevButtonRef = useRef<HTMLButtonElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const { ariaLiveRef } = useSwiperAccessibility({
    getSlideLabel: (current, total) =>
      tA11y('A11Y.PRODUCT_CAROUSEL.CURRENT_ITEM', { currentSlide: current, totalSlides: total }),
    getAriaLiveLabel: (start, end, total) =>
      tA11y('A11Y.PRODUCT_CAROUSEL.CURRENT_ITEM', {
        currentSlide: start === end ? start : `${start}-${end}`,
        totalSlides: total,
      }),
    swiper: swiperLiveRef.current,
    slides: products,
    // TODO: Enable inert for desktop multi-slide sliders once expected behavior is confirmed from GAIN
    // useInert: isWiderThanMd,
    useInert: false,
  });

  const handleAfterInit = (swiper): void => {
    swiperLiveRef.current = swiper;
  };

  return (
    <div
      className={clsx(styles.product_slider__swiper_wrap, wrapperClassName)}
      ref={slidesContainerRef}
      data-cy={`product-slider-${normalizedParentBlockTitle}`}
    >
      {shouldShowSkipLink && (
        <SkipLink
          label={`${tA11y('A11Y.SKIP_FORWARD')} ${tA11y('A11Y.PRODUCT_CAROUSEL', { totalSlides: products.length })}`}
          targetId={skipTargetId}
          awaitLazyContent={true}
        />
      )}
      <div
        className="visuallyhidden"
        aria-live="polite"
        aria-atomic="true"
        ref={ariaLiveRef}
      />
      <Button
        data-cy="product-slider-previous-slide-button"
        disableDefaultStyling
        className={clsx(
          'vc-d-none vc-d-md-inline-block',
          styles.product_slider__swiper_button,
          styles.product_slider__prev_button,
          hidePrevButton && styles.product_slider__swiper_button__hide,
          prevBtnClass,
        )}
        onClick={() => swiperRef.current.swiper.slidePrev()}
        ref={prevButtonRef}
      >
        {/* This span is used only to internationalize above button and is not shown on page */}
        <span hidden>click prev</span>
        <div className={clsx(styles.product_slider__prev_icon, styles.product_slider__controls_icon)} />
      </Button>
      <Swiper
        modules={[FreeMode, Mousewheel, A11y, Navigation, Keyboard]}
        onInit={(core) => {
          swiperRef.current = core.el;
        }}
        freeMode={{
          enabled: true,
          sticky: isWiderThanMd,
          momentumBounce: false,
        }}
        mousewheel={{
          forceToAxis: true,
        }}
        wrapperTag="ul"
        keyboard={{ enabled: true, onlyInViewport: true }}
        a11y={{
          prevSlideMessage: tA11y('A11Y.ICON.ARROW_PREVIOUS_ITEMS'),
          nextSlideMessage: tA11y('A11Y.ICON.ARROW_MORE_ITEMS'),
          slideLabelMessage: '{{index}} / {{slidesLength}}',
        }}
        onAfterInit={handleAfterInit}
        onProgress={(e) => setSlideProgress(e.progress)}
        watchSlidesProgress={true}
        shortSwipes={false}
        draggable
        slidesPerView={config.slidesPerView}
        spaceBetween={0}
        breakpoints={{
          [String(SLIDER_BREAKPOINTS.mobile)]: {
            slidesPerView: config.mobileSlidesPerView,
            spaceBetween: 0,
            centeredSlides: false,
          },
          [String(SLIDER_BREAKPOINTS.tablet)]: {
            slidesPerView: config.tabletSlidesPerView,
            spaceBetween: 0,
            centeredSlides: false,
          },
          [String(SLIDER_BREAKPOINTS.desktop)]: {
            slidesPerView: config.desktopSlidesPerView,
            spaceBetween: 0,
            centeredSlides: false,
          },
          [String(SLIDER_BREAKPOINTS.largeDesktop)]: {
            slidesPerView: config.largeDesktopSlidesPerView,
            spaceBetween: 0,
            centeredSlides: false,
          },
        }}
        slidesOffsetAfter={isWiderThanMd ? 0 : 20}
        slidesOffsetBefore={isWiderThanMd ? 0 : 20}
        navigation={{
          prevEl: `.${prevBtnClass}`,
          nextEl: `.${nextBtnClass}`,
        }}
        aria-label={tA11y('A11Y.PRODUCT_CAROUSEL.ALL_ITEMS', {
          productCarouselName: parentBlockTitle || '',
          groupItems: visibleSlides,
          totalItems: products.length,
        })}
        {...swiperProps}
      >
        {children}
      </Swiper>
      <Button
        data-cy="product-slider-next-slide-button"
        disableDefaultStyling
        className={clsx(
          'vc-d-none vc-d-md-inline-block',
          styles.product_slider__swiper_button,
          styles.product_slider__next_button,
          hideNextButton && styles.product_slider__swiper_button__hide,
          nextBtnClass,
        )}
        onClick={() => swiperRef.current.swiper.slideNext()}
        ref={nextButtonRef}
      >
        {/* This span is used only to internationalize above button and is not shown on page */}
        <span hidden>click next</span>
        <div className={clsx(styles.product_slider__next_icon, styles.product_slider__controls_icon)} />
      </Button>
      {shouldShowSkipLink && <SkipTarget id={skipTargetId} />}
    </div>
  );
};

export default ProductSlider;
